import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import QuoteHeader from "../../components/Quote/QuoteHeader";
import QuoteItemsTable from "../../components/Quote/QuoteTable";
import ServiceDetails from "../../components/Quote/QuoteServiceDetail";
import QuoteActions from "../../components/Quote/QuoteActions";
import PackageModal from "../../components/Modal/Quote/ServiceModal";
import FileModalLocation from "../../components/Modal/FileModalLocation";
import api from "../../../utils/axios";
import { getSpRunNumber } from "../../../utils/SamuiUtils";
import Swal from "sweetalert2";
import QuoteDocumentInfo from "../../components/Quote/QuoteDocumentInfo";

export default function AdminQuotePage() {
  const { Doc_Id } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLocked, setIsLocked] = useState(false);

  const [header, setHeader] = useState({
    Comp_Id: localStorage.getItem("company"),
    Doc_No: "",
    Doc_Date: new Date().toISOString().split("T")[0],
    Doc_Status: 1,
    Doc_Code: 6,
    Doc_Detail: "",
    Doc_Remark: "",
    AR_Id: null,
    AR_Name: "",
    ServiceAddr_Id: null,
    ServiceAddr_Name: "",
    ServiceAddr_FullText: "",
    DisplayName: "",
    IsVat: 0,
    Sale_Emp_Id: null,
  });

  const [serviceDetail, setServiceDetail] = useState({
    Contract_Period: "",
    Contract_Unit: "",
    Warranty: "",
    Warranty_Unit: "",
    Service_Time: "",
    Service_Unit: "",
    Payment_Condition: "",
  });
  const [subLocations, setSubLocations] = useState([]);

  //  ฟังก์ชัน format ที่อยู่ให้เหมือน ServiceLocationModal
  const formatAddress = (loc) => {
    return [
      loc.AddrNo || "",
      loc.SubDistrictName ? `ต.${loc.SubDistrictName}` : "",
      loc.DistrictName ? `อ.${loc.DistrictName}` : "",
      loc.ProvinceName ? `จ.${loc.ProvinceName}` : "",
      loc.Zipcode || "",
    ]
      .filter(Boolean)
      .join(" ");
  };

  //  โหลดใบเสนอราคามาแก้ไข
  useEffect(() => {
    const loadQuote = async () => {
      try {
        if (!Doc_Id) {
          //  NEW MODE → สร้างเลขที่
          const runNo = await getSpRunNumber("QT");
          if (runNo) setHeader((prev) => ({ ...prev, Doc_No: runNo }));
          return;
        }

        const res = await api.get(`/get-quote/${Doc_Id}`);
        const h = res.data.header;
        const d = res.data.details || [];
        const f = res.data.files || [];

        setHeader((prev) => ({
          ...prev,
          ...h,
          Doc_Date: h.Doc_Date ? h.Doc_Date.split("T")[0] : "",
          IsVat: h.IsVat ?? 0,
          ServiceAddr_FullText: h.ServiceAddr_FullText || "",
        }));

        setServiceDetail({
          Contract_Period: h.Contract_Period || "",
          Contract_Unit: h.Contract_Unit || "",
          Warranty: h.Warranty || "",
          Warranty_Unit: h.Warranty_Unit || "",
          Service_Time: h.Service_Time || "",
          Service_Unit: h.Service_Unit || "",
          Payment_Condition: h.Payment_Condition || "",
        });

        setItems(
          d.map((item) => ({
            Pack_Id: item.Item_Id || item.Pack_Id,
            Pack_Name: item.Item_Name,
            Item_Qty: Number(item.Item_Qty) || 1,
            Item_Unit: item.Item_Unit || "สินค้า",
            Item_Price_Unit: Number(item.Item_Price_Unit) || 0,
            Item_Discount: Number(item.Item_Discount) || 0,
            Item_Img: item.Item_Img || null,
            Item_Img_Url: null,
          }))
        );

        setSelectedFiles(
          f.map((file) => ({
            File_Id: file.F_Id,
            File_Name: file.File_Name,
            File_Url: file.File_Url,
            size: 1024,
            type: "image/jpeg",
            isFromDB: true,
            rawFile: null,
          }))
        );

        const subs = res.data.subLocations || [];
        setSubLocations(
          subs.map((s) => ({
            Sub_Id: s.Sub_Id,
            Sub_Name: s.Sub_Name || "",
            Qty: Number(s.Qty) || 0,
            Price_Unit: Number(s.Price_Unit) || 0,
            Discount: Number(s.Discount) || 0,
            IsVat: !!s.IsVat,
            Sub_Img: s.Sub_Img || null,
            Sub_Img_Url: s.Sub_Img_Url || null,
          }))
        );

        if (h.Doc_Status === 2) setIsLocked(true);
      } catch (err) {
        console.error("โหลดใบเสนอราคา error:", err);
        Swal.fire("ผิดพลาด!", "โหลดข้อมูลใบเสนอราคาไม่สำเร็จ", "error");
      }
    };

    loadQuote();
  }, [Doc_Id]);

  //  ถ้าเป็น Edit → โหลดข้อมูลที่อยู่ใหม่จาก DB ให้รูปแบบตรงกัน
  useEffect(() => {
    if (!Doc_Id || !header.AR_Id || !header.ServiceAddr_Id) return;

    const reloadAddress = async () => {
      try {
        const res = await api.get(`/get-tb-set-service-addr/${header.AR_Id}`);

        if (res.data.status === "OK") {
          const found = res.data.result.recordset.find(
            (x) => x.ServiceAddr_Id === header.ServiceAddr_Id
          );

          if (found) {
            setHeader((prev) => ({
              ...prev,
              ServiceAddr_FullText: formatAddress(found),
            }));
          }
        }
      } catch (err) {
        console.error("โหลดที่อยู่ (admin) ไม่สำเร็จ:", err);
      }
    };

    reloadAddress();
  }, [Doc_Id, header.AR_Id, header.ServiceAddr_Id]);

  //  Save
  const handleSave = async () => {
    try {
      if (!header.AR_Id) {
        Swal.fire("กรุณาเลือกลูกค้า", "", "warning");
        return;
      }

      if (!header.ServiceAddr_Id) {
        Swal.fire("กรุณาเลือกสถานที่บริการ", "", "warning");
        return;
      }

      if (!header.Sale_Emp_Id) {
        Swal.fire("กรุณาเลือกพนักงานขาย", "", "warning");
        return;
      }

      if (items.length === 0) {
        Swal.fire("กรุณาเพิ่มสินค้า/แพ็กเกจก่อนบันทึก", "", "warning");
        return;
      }
      if (subLocations.length > 0 && items.length > 1) {
        Swal.fire("เมื่อมีจุดย่อย จะมีได้แค่บริการเดียว", "", "warning");
        return;
      }
      const payload = {
        header: {
          ...header,
          ...serviceDetail,
          IsVat: header.IsVat ? 1 : 0,
          Doc_Date: header.Doc_Date ? new Date(header.Doc_Date) : null,
          Created_By_Id: localStorage.getItem("emp_id"),
          Created_By_Name: localStorage.getItem("name"),
        },
        details: items.map((i, idx) => {
          const base = i.Item_Qty * i.Item_Price_Unit - (i.Item_Discount || 0);
          const vat = header.IsVat ? base * 0.07 : 0;

          return {
            Line: idx + 1,
            Pack_Id: i.Pack_Id,
            Pack_Name: i.Pack_Name,
            Item_Qty: i.Item_Qty,
            Item_Unit: i.Item_Unit,
            Item_Price_Unit: i.Item_Price_Unit,
            Item_Discount: i.Item_Discount,
            Item_VAT: vat,
            Item_Total: base + vat,
            Item_Img: i.Item_Img || null,
          };
        }),
        subLocations: subLocations.map((s) => ({
          Sub_Name: s.Sub_Name,
          Qty: s.Qty,
          Price_Unit: s.Price_Unit,
          Discount: s.Discount,
          IsVat: !!s.IsVat,
          Sub_Img_Url: s.Sub_Img_Url || null,
          Sub_Img: s.Sub_Img || null,
        })),
      };

      const res = Doc_Id
        ? await api.put(`/update-quote/${Doc_Id}`, payload)
        : await api.post("/create-quote", payload);

      const newDocId = Doc_Id || res.data?.Doc_Id;

      const newFiles = selectedFiles.filter((f) => !f.isFromDB && f.rawFile);
      if (newFiles.length > 0 && newDocId) {
        const formData = new FormData();
        newFiles.forEach((f) => formData.append("files", f.rawFile));
        formData.append("Comp_Id", header.Comp_Id);
        formData.append("Upload_By", localStorage.getItem("name") || "Admin");
        formData.append("DocCode_ID", 6);
        formData.append("Doc_ID", newDocId);

        await api.post(`/upload-quote-file/${newDocId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Swal.fire({
      //   icon: "success",
      //   title: "บันทึกข้อมูลสำเร็จ",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

      navigate("/admin-quotelist");
    } catch (err) {
      console.error("save quote error:", err);
      Swal.fire("เกิดข้อผิดพลาด", err.message, "error");
    }
  };

  return (
    <div className="flex">
      <Sidebar />

      <main
        className="flex-1 h-screen overflow-auto bg-gray-50"
        style={{ marginLeft: "var(--sidebar-width)" }}
      >
        <Topbar
          breadcrumbs={[
            { label: "Admin", link: "/admin-quotelist" },
            { label: Doc_Id ? "แก้ไขใบเสนอราคา" : "สร้างใบเสนอราคาใหม่" },
          ]}
        />

        <div className="p-6 space-y-6 pt-20">
          <h1 className="text-xl font-bold">
            {Doc_Id ? "แก้ไขใบเสนอราคา (Admin)" : "สร้างใบเสนอราคาใหม่ (Admin)"}
          </h1>

          {/*  Header */}
          <QuoteHeader
            header={header}
            setHeader={setHeader}
            isAdmin
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            subLocations={subLocations}
            setSubLocations={setSubLocations}
            itemsCount={items.length}
            disabled={isLocked}
          />

          {/*  Items / Packages */}
          <QuoteItemsTable
            items={items}
            handleChange={(idx, field, value) => {
              const newItems = [...items];
              newItems[idx][field] =
                ["Item_Qty", "Item_Price_Unit", "Item_Discount"].includes(field)
                  ? Number(value) || 0
                  : value;
              setItems(newItems);
            }}
            handleDelete={(idx) =>
              setItems(items.filter((_, i) => i !== idx))
            }
            header={header}
            setHeader={setHeader}
            onAddItem={() => setModalOpen(true)}
            disabled={isLocked}
            maxItems={subLocations.length > 0 ? 1 : undefined}
            hasSubLocations={subLocations.length > 0}
          />

          {/*  Service detail */}
          <ServiceDetails
            serviceDetail={serviceDetail}
            setServiceDetail={setServiceDetail}
            disabled={isLocked}
          />

          <QuoteDocumentInfo
            header={{
              ...header,
              Doc_Detail: header.Doc_Detail ?? "",
              Doc_Remark: header.Doc_Remark ?? "",
            }}
            setHeader={setHeader}
          />

          {/*  Save */}
          <QuoteActions onSave={handleSave} disabled={isLocked} isAdmin />
        </div>
      </main>

      {/*  Select package modal */}
      {modalOpen && (
        <PackageModal
          isOpen={modalOpen}
          selectedIds={items.map((i) => i.Pack_Id)}
          onClose={() => setModalOpen(false)}
          onSelect={(selected) => {
            const selectedList =
              selected.type === "package"
                ? selected.packages || []
                : selected.items || [];

            const newItems = selectedList.map((p) => ({
              Pack_Id: selected.type === "package" ? p.Pack_Id : p.Item_Id,
              Pack_Name: selected.type === "package" ? p.Pack_Name : p.Item_Name,
              Item_Qty: 1,
              Item_Unit: selected.type === "package" ? "แพ็กเกจ" : "สินค้า",
              Item_Price_Unit: p.Pack_Price || p.Item_Price || 0,
              Item_Discount: 0,
              Item_Img: p.Item_Img || null,
              Item_Img_Url: null,
            }));

            setItems((prev) => [...prev, ...newItems]);
            setModalOpen(false);
          }}
        />
      )}

      {/*  File modal */}
      {showFileModal && (
        <FileModalLocation
          mode="E"
          showViewAttachModal={showFileModal}
          setShowViewAttachModal={setShowFileModal}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          Doc_Id={Doc_Id}
          docCodeId={6}
        />
      )}
    </div>
  );
}
